/*
 * fb-filters-collection.js — Frost Buddy custom collection filter rows.
 *
 * Architecture (from-first-principles refactor, see plan file):
 *
 *   init()   Runs once at DOMContentLoaded. Attaches the three listeners that
 *            survive DOM mutations: document click delegate, window resize,
 *            and a CAPTURE-PHASE scroll listener on the outer wrapper
 *            (.fb-filters-collection). The wrapper node survives innerHTML
 *            swaps of its descendants, so this listener never needs re-attach.
 *
 *   sync()   Idempotent post-mutation reconciliation. Called after init,
 *            after handleFilterClick's innerHTML swap, and at the tail of
 *            backfillDesigns. Runs reapplyKnownDesigns, updates scroll-btn
 *            visibility, and restores or centers scroll.
 *
 *   handleFilterClick()  saveScroll -> facetsForm.updateFiltersByURL -> fetch
 *            our section HTML and swap innerHTML -> sync() -> backfillDesigns.
 *
 * State authorities:
 *   Server (Liquid) owns --active, --muted, --pending, cleared hrefs, and the
 *   clear-actions hidden attr. This module never mutates those classes on
 *   confirmed tiles; the only DOM mutation is un-pending cached tiles (via
 *   reapplyKnownDesigns) and confirming/removing pending tiles in backfill.
 *
 * knownDesigns cache exists purely to prevent flicker when a filter click
 * collapses the page-1 primaries and the server would otherwise render
 * previously-confirmed tiles as pending for a frame.
 */

const knownDesigns = new Map();

// [FROST BUDDY] Known-visible caches for Locksmith reconciliation. Values are
// added whenever we observe a pill/tile rendered WITHOUT
// data-fb-locksmith-hidden — in the initial DOM, in a morphed DOM after a
// filter click, or in a backfilled page's DOM. Values persist across morphs
// so a Locksmith-hidden pill in a fresh SSR gets un-hidden if we've
// previously seen it visible in another filter context.
const knownVisibleDesigns = new Set();
const knownVisibleProductTypes = new Set();

const PILLS_SELECTOR = '.fb-filters-collection__pills';
const SWATCHES_SELECTOR = '.fb-filters-collection__swatches';
const SWATCHES_VIEWPORT_SELECTOR = '.fb-filters-collection__swatches-viewport';
const EYEBROW_SELECTOR = '.fb-filters-collection__eyebrow';
const PILL_SELECTOR = '.fb-filters-collection__pill';
const SWATCH_SELECTOR = '.fb-filters-collection__swatch';
const CLEAR_SELECTOR = '.fb-filters-collection__clear';
const WRAPPER_SELECTOR = '.fb-filters-collection';
const DESIGN_PARAM = 'filter.p.m.filters.design';
const PRODUCT_TYPE_PARAM = 'filter.p.m.filters.product_type';
const LOCKSMITH_HIDDEN_ATTR = 'data-fb-locksmith-hidden';
const SCROLL_KEYS = {
  pills: 'fb-filters-collection:pills-scroll',
  swatches: 'fb-filters-collection:swatches-scroll',
};

function getWrapper() {
  return document.querySelector(WRAPPER_SELECTOR);
}

function getSectionId() {
  const el = document.querySelector('.fb-filters-collection-section');
  if (!el || !el.id) return null;
  return el.id.replace(/^shopify-section-/, '');
}

function getFacetsForm() {
  return document.querySelector('facets-form-component');
}

function scrollKeyFor(row) {
  return row.classList.contains('fb-filters-collection__swatches')
    ? SCROLL_KEYS.swatches
    : SCROLL_KEYS.pills;
}

function rowHasActive(row) {
  const params = new URLSearchParams(window.location.search);
  // [FROST BUDDY] Rows carry their resolved facet param in data-fb-param —
  // collections can swap a row's facet via metafield (fb-shop-by-facets), so
  // the hardcoded defaults are only a fallback for markup predating that.
  const fallback = row.classList.contains('fb-filters-collection__swatches')
    ? DESIGN_PARAM
    : PRODUCT_TYPE_PARAM;
  return params.has(row.dataset.fbParam || fallback);
}

function saveScroll() {
  document.querySelectorAll(`${PILLS_SELECTOR}, ${SWATCHES_SELECTOR}`).forEach((row) => {
    try {
      sessionStorage.setItem(scrollKeyFor(row), String(row.scrollLeft));
    } catch (_) {
      /* sessionStorage may be blocked */
    }
  });
}

/**
 * Restore or center scroll for each row. Policy:
 *   - No filter active for this row      -> scrollLeft = 0, clear sessionStorage.
 *   - Filter active + saved sessionStorage -> apply the saved value.
 *   - Filter active + no saved value      -> center the active tile.
 */
function restoreOrCenterScroll() {
  document.querySelectorAll(`${PILLS_SELECTOR}, ${SWATCHES_SELECTOR}`).forEach((row) => {
    const key = scrollKeyFor(row);
    if (!rowHasActive(row)) {
      row.scrollLeft = 0;
      try { sessionStorage.removeItem(key); } catch (_) { /* ignore */ }
      return;
    }
    let saved = null;
    try { saved = sessionStorage.getItem(key); } catch (_) { /* ignore */ }
    if (saved !== null) {
      row.scrollLeft = parseFloat(saved) || 0;
      return;
    }
    centerActiveInRow(row);
  });
}

function centerActiveInRow(row) {
  const active = /** @type {HTMLElement | null} */ (
    row.querySelector(
      '.fb-filters-collection__pill--active, .fb-filters-collection__swatch--active'
    )
  );
  if (!active) return;
  if (row.scrollWidth <= row.clientWidth) return;
  // Defer one frame so layout (image loads, appends/removals) settles before
  // we measure offsetLeft.
  requestAnimationFrame(() => {
    const target = active.offsetLeft - (row.clientWidth - active.offsetWidth) / 2;
    row.scrollLeft = Math.max(0, target);
  });
}

/**
 * Snapshot every currently-confirmed swatch (has a real <img>, no pending
 * marker) into the knownDesigns cache. Called after successful backfill so
 * future morphs can restore those tiles instantly.
 */
function captureKnownDesigns() {
  document
    .querySelectorAll(`${SWATCH_SELECTOR}:not([data-fb-pending="true"])`)
    .forEach((tile) => {
      const design = /** @type {HTMLElement} */ (tile).dataset.fbDesign;
      const img = tile.querySelector('img');
      const src = img?.getAttribute('src');
      if (design && src) knownDesigns.set(design, src);
    });
}

/**
 * After a fresh morph, un-pend any tile we've previously confirmed, using
 * the cached image. This runs synchronously before browser paint so the
 * user never sees the "everything pending" intermediate frame.
 *
 * Muted state is NOT touched here — the server has already rendered the
 * correct --muted class on the pending placeholder for the current URL
 * context. Server remains the authority on muted-ness.
 */
function reapplyKnownDesigns() {
  document.querySelectorAll('[data-fb-pending="true"]').forEach((tile) => {
    const el = /** @type {HTMLElement} */ (tile);
    const design = el.dataset.fbDesign;
    const src = design ? knownDesigns.get(design) : null;
    if (!src) return;
    const imageSlot = el.querySelector('.fb-filters-collection__swatch-image');
    if (imageSlot) {
      const safeAlt = design.replace(/"/g, '&quot;');
      imageSlot.innerHTML = `<img src="${src}" alt="${safeAlt}" loading="lazy" width="92" height="92">`;
    }
    el.removeAttribute('data-fb-pending');
    el.classList.remove('fb-filters-collection__swatch--pending');
  });
}

/**
 * [FROST BUDDY] Locksmith visibility helpers.
 *
 * Server marks a pill/tile with data-fb-locksmith-hidden="true" when its
 * value has no accessible products in the current SSR context. That
 * context is filter-URL-dependent (collection.products iteration honors
 * URL filters), so a legit value can be transiently marked hidden after
 * a filter click even though it's accessible in another filter context.
 *
 * The knownVisible sets grow monotonically as we observe values rendered
 * without the hide flag — in the initial DOM, in subsequent morphs, and
 * in backfilled page 2+ HTML. Reconciliation un-hides any locally-hidden
 * pill/tile whose value we've previously seen visible.
 */
function growKnownVisibleFrom(root) {
  root
    .querySelectorAll(`${PILL_SELECTOR}[data-fb-ptype]:not([${LOCKSMITH_HIDDEN_ATTR}='true'])`)
    .forEach((el) => {
      const val = /** @type {HTMLElement} */ (el).dataset.fbPtype;
      if (val) knownVisibleProductTypes.add(val);
    });
  root
    .querySelectorAll(`${SWATCH_SELECTOR}[data-fb-design]:not([${LOCKSMITH_HIDDEN_ATTR}='true'])`)
    .forEach((el) => {
      const val = /** @type {HTMLElement} */ (el).dataset.fbDesign;
      if (val) knownVisibleDesigns.add(val);
    });
}

function unhideKnownVisibleInDocument() {
  document
    .querySelectorAll(`${PILL_SELECTOR}[data-fb-ptype][${LOCKSMITH_HIDDEN_ATTR}='true']`)
    .forEach((el) => {
      const val = /** @type {HTMLElement} */ (el).dataset.fbPtype;
      if (val && knownVisibleProductTypes.has(val)) {
        el.removeAttribute(LOCKSMITH_HIDDEN_ATTR);
      }
    });
  document
    .querySelectorAll(`${SWATCH_SELECTOR}[data-fb-design][${LOCKSMITH_HIDDEN_ATTR}='true']`)
    .forEach((el) => {
      const val = /** @type {HTMLElement} */ (el).dataset.fbDesign;
      if (val && knownVisibleDesigns.has(val)) {
        el.removeAttribute(LOCKSMITH_HIDDEN_ATTR);
      }
    });
}

function reconcileLocksmithVisibility() {
  growKnownVisibleFrom(document);
  unhideKnownVisibleInDocument();
}

/**
 * After backfill completes (or immediately if no backfill is needed), hide
 * the eyebrow + row wrapper for any row left with fewer than 2 accessible
 * (non-Locksmith-hidden) value pills/tiles. A filter needs 2+ options to be
 * useful, so this covers both the all-hidden case AND a row Locksmith-locked
 * down to a single accessible option — a per-visitor call that can't be made
 * server-side (the SSR is edge-cached and the accessible allowlist is only
 * page-1 complete). The genuinely-single-value case is already suppressed at
 * SSR (values.size > 1 gate), so this is the per-visitor Locksmith layer.
 * Non-value elements (e.g., the "All" pill) don't carry
 * data-fb-ptype/data-fb-design and are not counted.
 */
function hideEmptyLocksmithRows() {
  const rows = [
    { wrapperSel: PILLS_SELECTOR, valueSel: `${PILL_SELECTOR}[data-fb-ptype]` },
    { wrapperSel: SWATCHES_VIEWPORT_SELECTOR, valueSel: `${SWATCH_SELECTOR}[data-fb-design]` },
  ];
  rows.forEach(({ wrapperSel, valueSel }) => {
    document.querySelectorAll(wrapperSel).forEach((wrapper) => {
      const total = wrapper.querySelectorAll(valueSel).length;
      if (total === 0) return; // no facet values — SSR already suppressed
      const hidden = wrapper.querySelectorAll(
        `${valueSel}[${LOCKSMITH_HIDDEN_ATTR}='true']`
      ).length;
      const shouldHide = total - hidden < 2;
      wrapper.toggleAttribute('hidden', shouldHide);
      const prev = wrapper.previousElementSibling;
      if (prev && prev.matches(EYEBROW_SELECTOR)) {
        prev.toggleAttribute('hidden', shouldHide);
      }
    });
  });
}

function updateScrollBtnVisibility() {
  document.querySelectorAll('.fb-filters-collection__swatches-viewport').forEach((viewport) => {
    const row = viewport.querySelector(SWATCHES_SELECTOR);
    const prev = viewport.querySelector('[data-fb-scroll="prev"]');
    const next = viewport.querySelector('[data-fb-scroll="next"]');
    if (!row || !prev || !next) return;
    const canScroll = row.scrollWidth > row.clientWidth + 1;
    // Small threshold to survive sub-pixel rounding at the endpoints.
    const atStart = row.scrollLeft <= 1;
    const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1;
    prev.toggleAttribute('hidden', !canScroll || atStart);
    next.toggleAttribute('hidden', !canScroll || atEnd);
  });
}

/**
 * Idempotent post-mutation reconciliation. Runs after init, after the
 * innerHTML swap in handleFilterClick, and after backfillDesigns mutations.
 */
function sync() {
  reapplyKnownDesigns();
  reconcileLocksmithVisibility();
  restoreOrCenterScroll();
  updateScrollBtnVisibility();
}

async function handleFilterClick(link, event) {
  const href = link.getAttribute('href');
  if (!href) return;

  const facetsForm = getFacetsForm();
  const sectionId = getSectionId();

  // Progressive-enhancement fallback — if neither hook is available, let the
  // browser handle the navigation.
  if (!facetsForm && !sectionId) return;

  event.preventDefault();
  saveScroll();

  if (facetsForm) {
    // Pushes state and morphs main-collection. Fire-and-forget.
    facetsForm.updateFiltersByURL(href);
  } else {
    history.pushState({}, '', href);
  }

  if (sectionId) {
    try {
      // Direct fetch + innerHTML replace instead of sectionRenderer, whose
      // Idiomorph pass preserves DOM nodes it can't reconcile against the
      // new HTML. That would leave stale tiles (e.g. designs Shopify's
      // facet drops when a product_type filter is applied) hanging on at
      // the end of the row, breaking alphabetical ordering. Our section
      // has no live state to preserve, so a clean innerHTML swap is
      // correct AND faster. The outer .fb-filters-collection node itself
      // survives, so listeners attached to it (see init: capture-phase
      // scroll) live across every filter click.
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (res.ok) {
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector(WRAPPER_SELECTOR);
        const current = getWrapper();
        if (incoming && current) {
          current.innerHTML = incoming.innerHTML;
          const pages = incoming.getAttribute('data-fb-total-pages');
          if (pages) current.setAttribute('data-fb-total-pages', pages);
        }
      }
    } catch (_) {
      // Network / parse error — leave DOM as-is; pushState still moved us
      // and main-collection morphed.
    }
  }

  sync();
  captureKnownDesigns();
  backfillDesigns();
}

/**
 * Progressive per-page backfill. Since the Design filter is authoritative,
 * every tile that should render is already in the DOM at first paint —
 * some with real images, some with placeholders. This pass ONLY promotes
 * placeholders to real images as they're discovered on later pages.
 *
 * Guarantees:
 *   - Never adds tiles.
 *   - Never removes tiles.
 *   - Never reorders tiles.
 *
 * Anything not confirmed by the end of the fetch loop simply stays as a
 * placeholder — the filter click still works (the href is server-rendered
 * and preserved), only the thumbnail is missing.
 *
 * The loop continues through all pages even after the current pending set
 * clears, so knownDesigns learns as many designs as possible for smooth
 * cross-morph un-pending on subsequent filter clicks.
 */
async function backfillDesigns() {
  const wrapper = getWrapper();
  if (!wrapper) return;
  const totalPages = parseInt(wrapper.dataset.fbTotalPages || '1', 10);
  const sectionId = getSectionId();

  const hasPending = !!document.querySelector(
    `${SWATCHES_SELECTOR} [data-fb-pending="true"]`
  );
  const hasLocksmithHidden = !!document.querySelector(
    `[${LOCKSMITH_HIDDEN_ATTR}='true']`
  );

  // Nothing to fetch — no images to backfill, no hidden pills to try to
  // un-hide, or the collection fits on one page. Still finalize row-hide
  // in case an entire row is Locksmith-hidden after page-1 SSR.
  if (
    !Number.isFinite(totalPages) ||
    totalPages <= 1 ||
    !sectionId ||
    (!hasPending && !hasLocksmithHidden)
  ) {
    hideEmptyLocksmithRows();
    return;
  }

  for (let page = 2; page <= totalPages; page++) {
    let html;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', String(page));
      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!res.ok) continue;
      html = await res.text();
    } catch (_) {
      continue;
    }
    if (!html) continue;

    const doc = new DOMParser().parseFromString(html, 'text/html');

    // [FROST BUDDY] Grow known-visible caches from this backfilled page,
    // then un-hide any local pills/tiles the page proved accessible.
    // Do this BEFORE image promotion so subsequent logic sees the correct
    // hide state, and un-hiding takes effect even if wantsBySrc is empty.
    growKnownVisibleFrom(doc);
    unhideKnownVisibleInDocument();

    // Collect { design -> {src, alt} } from tiles on this page that have
    // real images and correspond to designs we're still missing locally.
    const wantsBySrc = new Map();
    doc.querySelectorAll(`${SWATCH_SELECTOR}[data-fb-design]`).forEach((el) => {
      const tile = /** @type {HTMLElement} */ (el);
      if (tile.dataset.fbPending === 'true') return;
      const design = tile.dataset.fbDesign;
      if (!design || wantsBySrc.has(design)) return;
      const img = tile.querySelector('img');
      const src = img?.getAttribute('src');
      if (!src) return;
      // Skip if already resolved locally (either as a real tile or by the
      // in-memory cache from earlier — either way we don't need to reload).
      const localTile = document.querySelector(
        `${SWATCH_SELECTOR}[data-fb-design="${CSS.escape(design)}"]`
      );
      if (!localTile) return;
      if (localTile.getAttribute('data-fb-pending') !== 'true' && !knownDesigns.has(design)) {
        // Real tile already, no need to touch — but do capture into cache
        // so future morphs benefit.
        knownDesigns.set(design, src);
        return;
      }
      wantsBySrc.set(design, {
        src,
        alt: img?.getAttribute('alt') || design,
      });
    });

    if (wantsBySrc.size === 0) continue;

    // Preload this page's images before mutating so the swap paints
    // instantly instead of showing a blank frame while <img> loads.
    await Promise.all(
      Array.from(wantsBySrc.values()).map(
        (info) =>
          new Promise((resolve) => {
            const preloader = new Image();
            preloader.onload = () => resolve();
            preloader.onerror = () => resolve();
            preloader.src = info.src;
          })
      )
    );

    // Promote the currently-pending tiles whose images we just resolved.
    wantsBySrc.forEach((info, design) => {
      const tile = /** @type {HTMLElement | null} */ (
        document.querySelector(
          `${SWATCH_SELECTOR}[data-fb-design="${CSS.escape(design)}"][data-fb-pending="true"]`
        )
      );
      knownDesigns.set(design, info.src);
      if (!tile) return; // may have been re-morphed away; cache still learned it
      const imageSlot = tile.querySelector('.fb-filters-collection__swatch-image');
      if (imageSlot) {
        imageSlot.innerHTML = `<img src="${info.src}" alt="${info.alt.replace(/"/g, '&quot;')}" loading="lazy" width="92" height="92">`;
      }
      tile.removeAttribute('data-fb-pending');
      tile.classList.remove('fb-filters-collection__swatch--pending');
    });
  }

  // [FROST BUDDY] Backfill complete — hide any row whose values are all
  // still Locksmith-hidden (nothing was proven accessible on any page).
  hideEmptyLocksmithRows();

  // Layout hasn't shifted (tiles stayed put, only slot contents changed),
  // but scroll-btn visibility is worth recomputing in case CSS pseudo-
  // elements on the placeholder measured differently. Cheap; idempotent.
  updateScrollBtnVisibility();
}

function onDocumentClick(event) {
  const target = /** @type {HTMLElement | null} */ (event.target);
  if (!target) return;

  // Scroll-arrow click (desktop) — handled inline; no navigation.
  const scrollBtn = target.closest('[data-fb-scroll]');
  if (scrollBtn instanceof HTMLButtonElement) {
    const viewport = scrollBtn.closest('.fb-filters-collection__swatches-viewport');
    const row = viewport?.querySelector(SWATCHES_SELECTOR);
    if (row) {
      const dir = scrollBtn.dataset.fbScroll === 'prev' ? -1 : 1;
      const step = Math.max(row.clientWidth * 0.7, 300);
      row.scrollBy({ left: dir * step, behavior: 'smooth' });
    }
    return;
  }

  const link = target.closest(
    `${PILL_SELECTOR}, ${SWATCH_SELECTOR}, ${CLEAR_SELECTOR}`
  );
  if (!(link instanceof HTMLAnchorElement)) return;
  // Respect modifier keys (open in new tab, etc.) — let the browser handle.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
    return;
  }
  handleFilterClick(link, event);
}

function init() {
  const wrapper = getWrapper();
  if (!wrapper) return;

  // Stable listeners — attached once, survive every innerHTML swap of
  // wrapper's descendants because wrapper itself is never replaced.
  document.addEventListener('click', onDocumentClick);
  window.addEventListener('resize', updateScrollBtnVisibility, { passive: true });
  // Scroll events don't bubble but DO capture; hosting the listener on the
  // stable wrapper means we catch scrolls on whatever swatches row is
  // currently mounted, including freshly-swapped ones. This is the fix for
  // "the left arrow doesn't always work after a filter click."
  wrapper.addEventListener('scroll', updateScrollBtnVisibility, { capture: true, passive: true });

  sync();
  captureKnownDesigns();
  backfillDesigns();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
